const MIME_TYPE_MAP = {
  csv: 'text/csv',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  jpeg: 'image/jpeg',
  jpg: 'image/jpeg',
  pdf: 'application/pdf',
  png: 'image/png',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
};

/**
 * @typedef {Object} HasReachedMaxFilesParams
 * @property {File[]} files
 * @property {number} filesCount
 * @property {number} maxFiles
 */

/**
 * @typedef {Object} HasExceededSizeParams
 * @property {File[]} files
 * @property {number} maxFileSize
 */

/**
 * @typedef {Object} RenameDuplicatedFilesParams
 * @property {Array<{files: File[]}>} attachments
 * @property {string} fileNameField
 * @property {File[]} selectedFiles
 */

/**
 * @param {string[]} extensions
 * @returns {string[]}
 */
export const buildAcceptedFilesArray = extensions => {
  if (!extensions || extensions.length === 0) return [];
  return [...new Set(extensions.map(ext => MIME_TYPE_MAP[ext.toLowerCase()]).filter(Boolean))];
};

const getFileExtension = file => {
  if (!file) return '';

  const lastDotIndex = file.lastIndexOf('.');
  return file.substring(lastDotIndex + 1).toLowerCase();
};

/**
 * @param {HasReachedMaxFilesParams} params
 * @returns {boolean}
 */
export const hasReachedMaxFiles = ({ files, filesCount, maxFiles }) => {
  const availableFilesCount = maxFiles - filesCount;
  return files.length > availableFilesCount;
};

/**
 * @param {HasExceededSizeParams} params
 * @returns {boolean}
 */
export const hasExceededSize = ({ files, maxFileSize }) => {
  if (!files || files.length === 0) {
    return false;
  }

  return files.some(file => file.size > maxFileSize);
};

/**
 * @param {RenameDuplicatedFilesParams} params
 * @returns {File[]}
 */
export const renameDuplicatedFiles = ({ attachments, fileNameField, selectedFiles }) => {
  const uniqueFileNames = new Set(
    attachments.flatMap(({ files }) => files.map(file => file[fileNameField]))
  );

  return selectedFiles.map(selectedFile => {
    const { name, type, lastModified } = selectedFile;

    if (!uniqueFileNames.has(name)) {
      return selectedFile;
    }

    const lastDotIndex = name.lastIndexOf('.');
    const fileExtension = getFileExtension(name);
    const baseFileName = name.substring(0, lastDotIndex);

    const newFileName = renameFile(baseFileName, fileExtension, uniqueFileNames);

    const updatedFile = new File([selectedFile], newFileName, {
      type,
      lastModified,
      path: newFileName
    });

    return updatedFile;
  });
};

const renameFile = (baseName, extension, uniqueFileNames, version = 2) => {
  const newFileName = `${baseName} (${version}).${extension}`;

  if (!uniqueFileNames.has(newFileName)) {
    return newFileName;
  }

  return renameFile(baseName, extension, uniqueFileNames, version + 1);
};

/**
 * @param {string} fileName
 * @returns {boolean}
 */
export const isValidFileName = fileName => {
  if (typeof fileName !== 'string' || fileName.length === 0) {
    return false;
  }

  // eslint-disable-next-line no-useless-escape
  const forbiddenCharacters = /[\/\\^<>~"\[\]:\+÷;,?'*@|$#%]/;

  return !forbiddenCharacters.test(fileName);
};

/**
 * @param {File[]} files
 * @returns {boolean}
 */
export const areFileNamesValid = files => {
  return files.every(({ name }) => isValidFileName(name));
};
